import { useEffect, useMemo, useState, useRef, Fragment } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import BaseReport from "../BaseReport.component";
import ReportModal from "../ReportModal.component";
import ReportService from "../../../services/reports/Report.service";
import { WeeklyActionRawData, WeeklyActionParsedData, WeekPoint, InflowWeekPoint } from "../../../interfaces/reports/WeeklyActionReport.interface";
import { MoneyDataItem, PercentDataItem, StringDataItem } from "../../../interfaces/reports/BaseReport.interface";
import TipContext from "../../../providers/GlobalTip";
import EpochMonthService from "../../../services/EpochMonth.service";

const TODAY_EPOCH_MONTH = EpochMonthService.getTodayEpochMonth();

interface WeeklyActionReportProps {
    clientId: string | number;
    epochMonth: number | null;
}

const HIERARCHY_OPTIONS: Record<string, string[]> = {
    "By Location": ["locationCode"],
    "Location By Class": ["locationCode", "classCode"],
    "Location By Category": ["locationCode", "category"],
    "Location by Category by Class": ["locationCode", "category", "classCode"],
    "By Class": ["classCode"],
    "Class by Location": ["classCode", "locationCode"],
    "By Category": ["category"],
    "Category by Location": ["category", "locationCode"],
    "Category by Class": ["category", "classCode"],
    "Category by Class by Location": ["category", "classCode", "locationCode"],
    "Class by Child": ["parentClientClassCode", "classCode"],
    "Class by Child by Location": ["parentClientClassCode", "classCode", "locationCode"],
    "Location by Class by Child": ["locationCode", "parentClientClassCode", "classCode"],
    "Category by Class by Child": ["category", "parentClientClassCode", "classCode"],
};

type CompareVs = "Plan" | "LY" | "L3M";

const parseCsvArray = (csv: string | null | undefined): number[] => {
    if (!csv) return [];
    return csv.split(",").map((v) => {
        const num = Number(v);
        return isNaN(num) ? 0 : num;
    });
};

const parseCsvSum = (csv: string | null | undefined, months: number): number => {
    if (!csv) return 0;
    return csv.split(",").slice(0, months).reduce((sum, val) => {
        const num = Number(val);
        return sum + (isNaN(num) ? 0 : num);
    }, 0);
};

// Moved data processing to a separate function. `salesByMonth`/`inflowsByMonth` are only
// used for the expanded-row detail view - they are plain arrays (not MoneyDataItem), so
// they don't survive ReportService.groupByHierarchy's aggregation. The expand feature
// re-derives its detail data directly from the ungrouped parsedData/parentParsedData
// (see getExpandDetail below) rather than relying on these fields post-grouping.
const processRawData = (rawData: WeeklyActionRawData[], forwardSpan: number): WeeklyActionParsedData[] => {
    if (!rawData || rawData.length === 0) return [];

    return rawData.map((item) => {
        const salesBackArr = parseCsvArray(item.salesBack);
        const salesBackLYArr = parseCsvArray(item.salesBackLY);
        const inflowBackArr = parseCsvArray(item.inflowBack);

        const salesByMonth: WeekPoint[] = salesBackArr.map((v, i) => ({ actual: v, ly: salesBackLYArr[i] ?? 0 }));
        salesByMonth.push({ actual: item.curSales ?? 0, ly: item.salesLY ?? 0 });

        const inflowsByMonth: InflowWeekPoint[] = inflowBackArr.map((v) => ({ actual: v }));
        inflowsByMonth.push({ actual: item.inflow ?? 0 });

        return {
            locationCode: item.locationCode,
            locationName: item.locationName,
            classCode: item.classCode,
            className: item.className,
            category: item.category,
            parentClientClassCode: item.parentClientClassCode ?? null,

            curSales: new MoneyDataItem(item.curSales),
            salesPlan: new MoneyDataItem(item.salesPlan),
            salesLY: new MoneyDataItem(item.salesLY),
            last3MonthsSales: new MoneyDataItem(item.last3MonthsSales),
            inventory: new MoneyDataItem(item.inventory),
            inventoryBomSpan: new MoneyDataItem(item.inventoryBomSpan),
            inflow: new MoneyDataItem(item.inflow),
            inflowPlan: new MoneyDataItem(item.inflowPlanRetail),
            planInflowForward: new MoneyDataItem(parseCsvSum(item.forwardInflowPlanRetail, forwardSpan)),
            onOrder: new MoneyDataItem(parseCsvSum(item.onOrder, forwardSpan)),
            otb: new MoneyDataItem(parseCsvSum(item.otb, forwardSpan)),

            wos: new StringDataItem(0),
            salesComparisonPercent: new PercentDataItem(0),

            salesByMonth,
            inflowsByMonth,
        };
    });
};

const reapplyWeeklyActionCalcs = (data: WeeklyActionParsedData[], compareVs: CompareVs): WeeklyActionParsedData[] => {
    return data.map((item) => {
        const curSales = Number(item.curSales?.value) || 0;
        const salesPlan = Number(item.salesPlan?.value) || 0;
        const salesLY = Number(item.salesLY?.value) || 0;
        const last3MonthsSales = Number(item.last3MonthsSales?.value) || 0;
        const inventory = Number(item.inventory?.value) || 0;

        // WOS: inventory / average weekly sales over the trailing 3 months
        const weeklySalesAverage = last3MonthsSales / 12;
        const wos = weeklySalesAverage > 0 ? Math.round((inventory / weeklySalesAverage) * 10) / 10 : 0;

        const base = compareVs === "LY" ? salesLY : compareVs === "L3M" ? (last3MonthsSales / 3) : salesPlan;
        const salesComparisonPercent = base > 0 ? Math.round(((curSales - base) / base) * 100) : 0;

        return {
            ...item,
            wos: new StringDataItem(wos),
            salesComparisonPercent: new PercentDataItem(salesComparisonPercent),
        };
    });
};

interface ExpandDetail {
    locations: Array<{ name: string; sales: number; inventory: number; otb: number; wos: number }>;
    salesByMonth: WeekPoint[];
    inflowsByMonth: InflowWeekPoint[];
}

// Re-derives the expanded-row detail directly from the ungrouped parsed data (matching this
// row's hierarchy field values), since grouping strips non-MoneyDataItem fields. Subtotal/total
// rows have their not-yet-grouped hierarchy fields blanked to "" (or null for
// parentClientClassCode) - those fields are treated as wildcards, so a subtotal row resolves to
// every raw row underneath it rather than just a single leaf combination.
const getExpandDetail = (item: WeeklyActionParsedData, hierarchy: string[], sourceData: WeeklyActionParsedData[]): ExpandDetail => {
    const matches = sourceData.filter((r) => hierarchy.every((field) => {
        if (field === "parentClientClassCode") {
            if (item.parentClientClassCode === null) {
                if (item.classCode === "") return true; // grand-total row: no filter
                return r.classCode === item.classCode;
            }
            return r.parentClientClassCode === item.parentClientClassCode;
        }
        const val = (item as any)[field];
        if (val === "" || val === undefined) return true; // not-yet-grouped level: no filter
        return (r as any)[field] === val;
    }));

    const byLocation = new Map<string, { name: string; sales: number; inventory: number; otb: number; last3MonthsSales: number }>();
    for (const m of matches) {
        const key = m.locationCode;
        const existing = byLocation.get(key) ?? { name: m.locationName || m.locationCode, sales: 0, inventory: 0, otb: 0, last3MonthsSales: 0 };
        existing.sales += Number(m.curSales?.value) || 0;
        existing.inventory += Number(m.inventory?.value) || 0;
        existing.otb += Number(m.otb?.value) || 0;
        existing.last3MonthsSales += Number(m.last3MonthsSales?.value) || 0;
        byLocation.set(key, existing);
    }
    const locations = Array.from(byLocation.values()).map((loc) => {
        const weeklyAvg = loc.last3MonthsSales / 12;
        const wos = weeklyAvg > 0 ? Math.round((loc.inventory / weeklyAvg) * 10) / 10 : 0;
        return { name: loc.name, sales: loc.sales, inventory: loc.inventory, otb: loc.otb, wos };
    }).sort((a, b) => b.sales - a.sales);

    const monthCount = matches[0]?.salesByMonth?.length ?? 0;
    const salesByMonth: WeekPoint[] = Array.from({ length: monthCount }, (_, i) => ({
        actual: matches.reduce((sum, m) => sum + (m.salesByMonth[i]?.actual ?? 0), 0),
        ly: matches.reduce((sum, m) => sum + (m.salesByMonth[i]?.ly ?? 0), 0),
    }));
    const inflowMonthCount = matches[0]?.inflowsByMonth?.length ?? 0;
    const inflowsByMonth: InflowWeekPoint[] = Array.from({ length: inflowMonthCount }, (_, i) => ({
        actual: matches.reduce((sum, m) => sum + (m.inflowsByMonth[i]?.actual ?? 0), 0),
    }));

    return { locations, salesByMonth, inflowsByMonth };
};

function fmt$(n: number) {
    return `$${Math.round(n).toLocaleString()}`;
}

// Expanded row detail panel: location breakdown + trailing sales/inflow-by-month history.
const WeeklyActionExpandedRow = ({ detail, wosTarget }: { detail: ExpandDetail; wosTarget: number }) => {
    const totalSales = detail.locations.reduce((s, l) => s + l.sales, 0);
    const totalInventory = detail.locations.reduce((s, l) => s + l.inventory, 0);
    const totalOtb = detail.locations.reduce((s, l) => s + l.otb, 0);
    const avgWos = detail.locations.length
        ? Math.round((detail.locations.reduce((s, l) => s + l.wos, 0) / detail.locations.length) * 10) / 10
        : 0;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 p-3 bg-gray-50">
            <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Summary</p>
                <div className="grid grid-cols-2 gap-1.5">
                    <div className="bg-white border border-gray-200 rounded px-2 py-1">
                        <p className="text-[10px] text-gray-400 leading-tight">Sales</p>
                        <p className="text-xs font-bold text-gray-900 leading-tight">{fmt$(totalSales)}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded px-2 py-1">
                        <p className="text-[10px] text-gray-400 leading-tight">Inventory</p>
                        <p className="text-xs font-bold text-gray-900 leading-tight">{fmt$(totalInventory)}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded px-2 py-1">
                        <p className="text-[10px] text-gray-400 leading-tight">WOS / Target</p>
                        <p className={`text-xs font-bold leading-tight ${wosTarget > 0 && avgWos < wosTarget ? "text-red-600" : "text-emerald-600"}`}>{avgWos} / {wosTarget}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded px-2 py-1">
                        <p className="text-[10px] text-gray-400 leading-tight">OTB</p>
                        <p className="text-xs font-bold text-emerald-600 leading-tight">{fmt$(totalOtb)}</p>
                    </div>
                </div>
            </div>

            <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Location Breakdown</p>
                <table className="w-full text-[11px]">
                    <thead>
                        <tr>
                            <th className="text-left font-medium text-gray-400 pb-0.5 border-b border-gray-200">Location</th>
                            <th className="text-right font-medium text-gray-400 pb-0.5 border-b border-gray-200">Sales</th>
                            <th className="text-right font-medium text-gray-400 pb-0.5 border-b border-gray-200">Inv</th>
                            <th className="text-right font-medium text-gray-400 pb-0.5 border-b border-gray-200">WOS</th>
                            <th className="text-right font-medium text-gray-400 pb-0.5 border-b border-gray-200">OTB</th>
                        </tr>
                    </thead>
                    <tbody>
                        {detail.locations.length === 0 ? (
                            <tr><td colSpan={5} className="text-center text-gray-400 py-2">No data</td></tr>
                        ) : detail.locations.map((loc) => (
                            <tr key={loc.name}>
                                <td className="py-0.5 border-b border-gray-100 font-medium text-gray-700">{loc.name}</td>
                                <td className="py-0.5 border-b border-gray-100 text-right font-semibold text-gray-900">{fmt$(loc.sales)}</td>
                                <td className="py-0.5 border-b border-gray-100 text-right text-gray-600">{fmt$(loc.inventory)}</td>
                                <td className="py-0.5 border-b border-gray-100 text-right text-gray-600">{loc.wos}</td>
                                <td className="py-0.5 border-b border-gray-100 text-right font-semibold text-emerald-600">{fmt$(loc.otb)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Sales &amp; Inflow by Month</p>
                <table className="w-full text-[11px]">
                    <thead>
                        <tr>
                            <th className="text-left font-medium text-gray-400 pb-0.5 border-b border-gray-200">Mo</th>
                            <th className="text-right font-medium text-gray-400 pb-0.5 border-b border-gray-200">Sales</th>
                            <th className="text-right font-medium text-gray-400 pb-0.5 border-b border-gray-200">Sales LY</th>
                            <th className="text-right font-medium text-gray-400 pb-0.5 border-b border-gray-200">Inflow</th>
                        </tr>
                    </thead>
                    <tbody>
                        {detail.salesByMonth.length === 0 ? (
                            <tr><td colSpan={4} className="text-center text-gray-400 py-2">No data</td></tr>
                        ) : detail.salesByMonth.map((w, i) => (
                            <tr key={i}>
                                <td className="py-0.5 border-b border-gray-100 text-gray-600">{i === detail.salesByMonth.length - 1 ? "Cur" : `Mo -${detail.salesByMonth.length - 1 - i}`}</td>
                                <td className="py-0.5 border-b border-gray-100 text-right font-semibold text-gray-900">{fmt$(w.actual)}</td>
                                <td className="py-0.5 border-b border-gray-100 text-right text-gray-400">{fmt$(w.ly)}</td>
                                <td className="py-0.5 border-b border-gray-100 text-right text-gray-600">{fmt$(detail.inflowsByMonth[i]?.actual ?? 0)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const WeeklyActionReport = ({ clientId, epochMonth }: WeeklyActionReportProps) => {
    const globalTip = TipContext.useMessage();
    const dataRef = useRef<WeeklyActionRawData[]>([]);
    const parentDataRef = useRef<WeeklyActionRawData[]>([]);
    const [parsedData, setParsedData] = useState<WeeklyActionParsedData[]>([]);
    const [parentParsedData, setParentParsedData] = useState<WeeklyActionParsedData[]>([]);
    const [sortedData, setSortedData] = useState<WeeklyActionParsedData[]>([]);
    const [loading, setLoading] = useState(true);
    const [hierarchy, setHierarchy] = useState<string[] | null>(["classCode"]);
    const [forwardSpan, setForwardSpan] = useState<number>(1);
    const [wosTarget, setWosTarget] = useState<number>(5);
    const [compareVs, setCompareVs] = useState<CompareVs>("Plan");
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    const [locationFilter, setLocationFilter] = useState<string[]>([]);
    const [classFilter, setClassFilter] = useState<string[]>([]);
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            ReportService.getWeeklyActionReportData({ clientId, epochMonth, includeChildClasses: true }).then((response) => {
                if (response) {
                    dataRef.current = response.data ?? [];
                    parentDataRef.current = response.parentLevelData ?? [];

                    const processed = processRawData(response.data ?? [], forwardSpan);
                    const processedParent = processRawData(response.parentLevelData ?? [], forwardSpan);
                    setParsedData(processed);
                    setParentParsedData(processedParent);

                    const useParent = hierarchy?.includes("parentClientClassCode");
                    let sorted = ReportService.groupByHierarchy(useParent ? processedParent : processed, hierarchy ?? ["classCode"]);
                    sorted = reapplyWeeklyActionCalcs(sorted, compareVs);
                    setSortedData(sorted);
                    setExpandedRows(new Set());
                } else {
                    dataRef.current = [];
                    parentDataRef.current = [];
                    setParsedData([]);
                    setParentParsedData([]);
                    setSortedData([]);
                }
                setLoading(false);
            }).catch(() => {
                if (globalTip) globalTip.show("Failed to fetch data");
                setLoading(false);
            });
        };
        fetchData();
    }, [clientId, epochMonth]);

    useEffect(() => {
        const newParsedData = processRawData(dataRef.current, forwardSpan);
        const newParentParsedData = processRawData(parentDataRef.current, forwardSpan);
        setParsedData(newParsedData);
        setParentParsedData(newParentParsedData);
    }, [forwardSpan]);

    useEffect(() => {
        const useParent = hierarchy?.includes("parentClientClassCode");
        const sourceData = useParent ? parentParsedData : parsedData;

        if (sourceData.length > 0) {
            let newSortedData = ReportService.sortAndFilterData(
                sourceData,
                hierarchy ?? ["classCode"],
                locationFilter,
                classFilter,
                categoryFilter
            );
            newSortedData = reapplyWeeklyActionCalcs(newSortedData, compareVs);
            setSortedData(newSortedData);
            setExpandedRows(new Set());
        }
    }, [hierarchy, parsedData, parentParsedData, locationFilter, classFilter, categoryFilter, compareVs]);

    let hierarchyColumns = [];
    for (let i = 0; i < (hierarchy?.length ?? 0); i++) {
        hierarchyColumns.push({
            name: "",
            sortable: false,
            customClassName: i === 0
                ? "px-4 py-2 bg-gray-500 text-white"
                : "px-4 py-2 bg-purple-100 border-purple-700 text-purple-800"
        });
    }

    const compareLabel = compareVs === "LY" ? "Vs LY %" : compareVs === "L3M" ? "Vs L3M Avg %" : "Vs Plan %";

    const columnDefs: Array<{ name: string; value: (item: WeeklyActionParsedData) => string | number }> = [
        { name: "Sales", value: (item) => item.curSales?.formatted ?? "" },
        { name: "Sales Plan", value: (item) => item.salesPlan?.formatted ?? "" },
        { name: "Sales LY", value: (item) => item.salesLY?.formatted ?? "" },
        { name: compareLabel, value: (item) => item.salesComparisonPercent?.formatted ?? "" },
        { name: "Inventory", value: (item) => item.inventory?.formatted ?? "" },
        { name: "Inventory BOM", value: (item) => item.inventoryBomSpan?.formatted ?? "" },
        { name: "WOS", value: (item) => item.wos?.formatted ?? "" },
        { name: "Inflow", value: (item) => item.inflow?.formatted ?? "" },
        { name: "Inflow Plan", value: (item) => item.inflowPlan?.formatted ?? "" },
        { name: "On Order", value: (item) => item.onOrder?.formatted ?? "" },
        { name: "OTB", value: (item) => item.otb?.formatted ?? "" },
    ];

    const columns = [
        ...hierarchyColumns,
        ...columnDefs.map(({ name }) => ({ name, sortable: false })),
    ];

    const tableData = useMemo(() => {
        return sortedData.map((item) => {
            const labelRows = ReportService.hierarchyItemToVerticalDisplayNames(item, hierarchy ?? ["classCode"]);
            return [
                ...labelRows,
                ...columnDefs.map((col) => col.value(item)),
            ];
        });
    }, [sortedData, hierarchy, compareVs]);

    const toggleExpand = (idx: number) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx); else next.add(idx);
            return next;
        });
    };

    const nonNullEpochMonth = epochMonth ?? TODAY_EPOCH_MONTH;
    const activeHierarchy = hierarchy ?? ["classCode"];
    const activeSourceData = activeHierarchy.includes("parentClientClassCode") ? parentParsedData : parsedData;

    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-center">
            <h2 className="text-2xl font-bold mb-4">{`Weekly Action Report - ${EpochMonthService.epochMonthToMMMYY(nonNullEpochMonth)}`}</h2>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div className="h-full max-w-[1500px]">
                    <BaseReport
                        title="Weekly Action Report"
                        tableColumns={columns.map((col) => col.name)}
                        tableData={tableData}
                        fileName={`Weekly_Action_Report_${new Date().toLocaleDateString()}`}
                        setHierarchy={setHierarchy}
                        hierarchy={hierarchy}
                        hierarchyOptions={HIERARCHY_OPTIONS}

                        parsedData={parsedData}
                        filteredLocationCodes={locationFilter}
                        filteredClassCodes={classFilter}
                        setFilteredLocationCodes={setLocationFilter}
                        setFilteredClassCodes={setClassFilter}
                        filteredCategories={categoryFilter}
                        setFilteredCategories={setCategoryFilter}

                        onApplyFilters={() => {
                            const processed = processRawData(dataRef.current, forwardSpan);
                            setParsedData(processed);
                        }}
                    />

                    <div className="flex flex-row flex-wrap items-center space-x-4 mb-4 ml-8">
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Compare Sales Vs</span>
                            <div className="h-10 flex items-center space-x-1 bg-gray-100 rounded-md p-1">
                                {(["Plan", "LY", "L3M"] as const).map((option) => (
                                    <button
                                        key={option}
                                        type="button"
                                        onClick={() => setCompareVs(option)}
                                        className={`px-3 py-1 text-[13px] font-medium rounded ${compareVs === option ? "bg-white shadow text-gray-900" : "text-gray-500"}`}
                                    >
                                        {option === "L3M" ? "L3M Avg" : option}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">On Order / OTB Span (Months)</span>
                            <input
                                type="number"
                                value={forwardSpan}
                                onChange={e => setForwardSpan(Math.min(5, Math.max(1, Number(e.target.value) || 1)))}
                                min={1}
                                max={5}
                                step={1}
                                className="h-10 w-32 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">WOS Target (Weeks)</span>
                            <input
                                type="number"
                                value={wosTarget}
                                onChange={e => setWosTarget(Math.max(0, Number(e.target.value) || 0))}
                                min={0}
                                step={0.5}
                                className="h-10 w-32 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            />
                        </div>
                    </div>

                    <div className="overflow-auto max-h-[70vh]">
                        <table className="w-full text-sm border-collapse">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-slate-700 text-white">
                                    <th className="px-2 py-2 w-8"></th>
                                    {columns.map((col, i) => (
                                        <th key={i} className="px-4 py-2 text-center font-medium">{col.name}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((item, idx) => {
                                    const rowLabels = ReportService.hierarchyItemToVerticalDisplayNames(item, activeHierarchy);
                                    const rowValues = columnDefs.map((col) => col.value(item));
                                    const rowClass = ReportService.getRowClassName([...rowLabels, ...rowValues], activeHierarchy);
                                    const expanded = expandedRows.has(idx);

                                    return (
                                        <Fragment key={idx}>
                                            <tr className={`${rowClass ?? (idx % 2 === 0 ? "bg-white" : "bg-gray-50")} border-b border-gray-200`}>
                                                <td className="px-2 py-2 text-center">
                                                    <button onClick={() => toggleExpand(idx)} className="text-gray-500 hover:text-gray-800" aria-label="Expand row">
                                                        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                    </button>
                                                </td>
                                                {rowLabels.map((label, i) => (
                                                    <td key={i} className="px-4 py-2 text-left font-medium">{label}</td>
                                                ))}
                                                {rowValues.map((val, i) => (
                                                    <td key={i} className="px-4 py-2 text-right">{val}</td>
                                                ))}
                                            </tr>
                                            {expanded && (
                                                <tr>
                                                    <td colSpan={1 + rowLabels.length + rowValues.length} className="p-0">
                                                        <WeeklyActionExpandedRow
                                                            detail={getExpandDetail(item, activeHierarchy, activeSourceData)}
                                                            wosTarget={wosTarget}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

interface WeeklyActionReportModalProps {
    clientId: string | number;
    epochMonth: number | null;
    onClose: () => void;
}

const WeeklyActionReportModal = ({ clientId, epochMonth, onClose }: WeeklyActionReportModalProps) => {
    return (
        <ReportModal
            onClose={onClose}
        >
            <WeeklyActionReport clientId={clientId} epochMonth={epochMonth} />
        </ReportModal>
    );
};

export { WeeklyActionReport, WeeklyActionReportModal };
